import { useContext, useEffect, useMemo, useRef, useState } from "react"

import { SimpleProgressBar } from "@brainfinance/icash-component-library"
import { postMessageSender } from "../../../Helpers/PostMessage"
import { getLoanStatus, getUser, getLatestIdentityVerification } from "../../../Helpers/Requests"
import { RequestsContext } from "../../../Helpers/Reducers/Requests"
import { InstantLoanContext } from "../../../Helpers/Reducers/InstantLoan"
import { goToRoutePath } from "../../../Helpers/Routes"
import { useNavigate, useLocation, Location } from "react-router-dom"
import { MessagesContext } from "../../../Helpers/Reducers/MessagesData"
import { UserContext } from "../../../Helpers/Reducers/User"

type LocationWithState = Omit<Location, 'state'> & { state: { [key: string]: any } };

const MAXIMUM = 120

function randomIndex(pickedIndices: number[]): number | undefined {
  if (pickedIndices.length === 0) return

  const internalIndex = Math.floor(Math.random() * pickedIndices.length)
  const index = pickedIndices.splice(internalIndex, 1)

  return index[0]
}

function useDynamicMessages({ messages = [], period = 10 }: { messages: string[]; period: number }) {
  const pickedIndexes = useRef<number[]>([])

  const [message, setMessage] = useState(() => {
    if (messages.length > 0) {
      messages.forEach((_, index) => {
        if (index === 0) return

        pickedIndexes.current.push(index)
      })

      return messages[0]
    }

    return `We’re reviewing your application...`
  })

  function updateMessage(timeInSec: number) {
    if (!messages || messages.length === 0 || period === undefined || period === 0 || timeInSec % period !== 0) {
      return
    }

    const index = randomIndex(pickedIndexes.current)

    if (index === undefined || index > messages.length - 1) {
      return
    }

    setMessage(messages[index])
  }

  return {
    message,
    updateMessage,
  }
}

export function ReviewInProgress() {
  const {
    state: { token },
  } = useContext(RequestsContext)
  const {
    state: { applicationReviewMessages },
  } = useContext(MessagesContext)
  const { dispatch: loanDispatch } = useContext(InstantLoanContext)
  const navigate = useNavigate()
  const { pathname, state } = useLocation() as LocationWithState;
  const now = useRef<number>(Date.now())
  const { dispatch: userDispatch } = useContext(UserContext)

  const { message, updateMessage } = useDynamicMessages({
    messages: applicationReviewMessages,
    period: 10,
  })

  const [timerEnd, setTimerEnds] = useState(false)
  const [seconds, setSeconds] = useState(0)

  useEffect(() => {
    if (state.visited) {
      postMessageSender({
        event: "return"
      })

      return;
    }

    const interval = setInterval(() => {
      setSeconds((prev) => {
        if (prev === MAXIMUM) {
          return prev
        }

        const diffInSec = Math.trunc((Date.now() - now.current) / 1000)

        return diffInSec >= MAXIMUM ? MAXIMUM : diffInSec
      })
    }, 1000)

    postMessageSender({
      event: "marketing",
      payload: {
        status: "screen",
        data: {
          categoryName: "Application",
          pathName: "app_review_in_progress_loaded",
        },
      },
    })
    return () => {
      clearInterval(interval)
    }
  }, [])

  useEffect(() => {
    if (seconds === MAXIMUM) {
      setTimerEnds(true)
    }

    updateMessage(seconds)
  }, [seconds])

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const instantLoan = await getLoanStatus(token)

        if (instantLoan?.status === "pendingSignature") {
          postMessageSender({
            event: "goToApplicationContract",
          })
          clearInterval(interval)
          return
        }

        if (instantLoan?.status !== "activeApplication") {
          return
        }

        const user = await getUser(token)

        if (user?.result.previousLoanTransactionNotFound) {
          clearInterval(interval)

          userDispatch({
            type: "setUser",
            payload: { user: user.result },
          })

          navigate("/payment-verification", { replace: true })
          return
        }

        if (user?.result.applicationStatusId === 20) {
          // The application is created/updated while the user waits here, so the user in
          // context (loaded once at app boot) is stale — refresh it before leaving this
          // page, otherwise identity verification reads an outdated applicationId.
          userDispatch({
            type: "setUser",
            payload: { user: user.result },
          })

          const latestIdentityVerification = await getLatestIdentityVerification(token)
          const verificationStatus = latestIdentityVerification.data?.status

          clearInterval(interval)

          if (latestIdentityVerification.data?.workflowType) {
            loanDispatch({
              step: "workflowType",
              payload: { workflowType: latestIdentityVerification.data.workflowType },
            })
          }

          switch (verificationStatus) {
            case "approved":
            case "manual_review":
              return navigate("/identity-verification/success", { state: { title: "Loan in review" } })

            case "in_progress":
            case "processing_result":
            case "awaiting_decision":
              return navigate("/identity-verification/verify", {
                state: { title: "Identity verification" }
              })

            case "abandoned":
              return navigate("/identity-verification/error", {
                state: {
                  title: "Identity verification",
                  status: "verification_incomplete",
                }
              })

            case "pending_retry":
              return navigate("/identity-verification/error", {
                state: {
                  title: "Identity verification",
                  status: "try_again",
                  attemptNumber: latestIdentityVerification.data?.retryCount,
                  maxRetryAttempts: latestIdentityVerification.data?.maxRetryAttempts,
                }
              })

            case "expired":
              return navigate("/identity-verification/error", {
                state: {
                  title: "Identity verification",
                  status: "session_expired",
                }
              })

            case "declined":
            case "withdrawn":
              break

            case "pending":
            default:
              return navigate("/identity-verification", {
                state: { title: "Identity verification" }
              })
          }
        } else if (user?.result.applicationStatusId === 30) {
          userDispatch({
            type: "setUser",
            payload: { user: user.result },
          })
          clearInterval(interval)
          return navigate("/identity-verification/success", { state: { title: "Loan in review" } })
        }
      } catch (error) {
        console.error("Error checking loan status:", error)
      }
    }, 5000)
    return () => {
      clearInterval(interval)
    }
  }, [token])

  useEffect(() => {
    if (timerEnd) {
      getLoanStatus(token).then((instantLoan) => {
        if (instantLoan?.status === "pendingSignature") {
          //send message for going to contract-signature
          postMessageSender({
            event: "goToApplicationContract",
          })
        } else {
          navigate(location.pathname, { state: { ...state, visited: true }, replace: true });
          goToRoutePath(navigate, 'loan-preapproved')
        }
      })
    }
  }, [timerEnd, token, pathname, navigate])

  const timerFormated = useMemo(() => {
    const secondsReverted = MAXIMUM - seconds
    const minuteTmp = Math.floor(secondsReverted / 60)
    const secondsTmp = secondsReverted % 60

    return `${minuteTmp >= 10 ? minuteTmp : `0${minuteTmp}`}:${secondsTmp >= 10 ? secondsTmp : `0${secondsTmp}`}`
  }, [seconds])

  return (
    <div className="max-w-[622px]">
      <p className="caption-light-no-color !text-[#13141A] !mb-[22px]">
        Congratulations! You're one step away from being approved for your loan.
      </p>
      <div className="flex flex-row items-center gap-2">
        <SimpleProgressBar completed={seconds} goal={MAXIMUM} />
        <div className="w-[120px]">
          <p className="main-title-caption !text-primary">{timerFormated}</p>
        </div>
      </div>
      <p className="caption-medium-no-colors !text-[#13141A] !mt-[16px]">{message}</p>
    </div>
  )
}
